/**
 * Deterministic randomness. Spec §5: explicit seeded PRNG, never Math.random().
 *
 * Phase 1 has no daily seed — the face is hand-built — but every stochastic call
 * in the codebase already draws from a stream so that Phase 2's generator and
 * Phase 4's server re-simulation inherit determinism for free.
 */

export type Rng = () => number

/** mulberry32 — small, fast, good enough distribution for terrain work. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0
  return function () {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Hash a string (e.g. "2026-08-13") into a 32-bit seed. Phase 4 uses this. */
export function hashString(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/**
 * Classic 2D Perlin gradient noise over a seeded permutation table.
 * Gradient noise rather than value noise: the terrain reads as ridges and
 * drainages instead of an axis-aligned lattice, which matters a lot when the
 * player is meant to route-read the surface.
 */
export class Noise2D {
  private readonly perm = new Uint8Array(512)

  constructor(rng: Rng) {
    const p = new Uint8Array(256)
    for (let i = 0; i < 256; i++) p[i] = i
    // Fisher-Yates from the seeded stream.
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1))
      const tmp = p[i]
      p[i] = p[j]
      p[j] = tmp
    }
    for (let i = 0; i < 512; i++) this.perm[i] = p[i & 255]
  }

  private static grad(hash: number, x: number, y: number): number {
    // 8 gradient directions from the low 3 bits.
    switch (hash & 7) {
      case 0: return x + y
      case 1: return x - y
      case 2: return -x + y
      case 3: return -x - y
      case 4: return x
      case 5: return -x
      case 6: return y
      default: return -y
    }
  }

  /** Perlin noise in roughly [-1, 1]. */
  sample(x: number, y: number): number {
    const xi = Math.floor(x)
    const yi = Math.floor(y)
    const xf = x - xi
    const yf = y - yi
    const X = xi & 255
    const Y = yi & 255

    const u = xf * xf * xf * (xf * (xf * 6 - 15) + 10)
    const v = yf * yf * yf * (yf * (yf * 6 - 15) + 10)

    const p = this.perm
    const aa = p[p[X] + Y]
    const ab = p[p[X] + Y + 1]
    const ba = p[p[X + 1] + Y]
    const bb = p[p[X + 1] + Y + 1]

    const x1 = lerpN(Noise2D.grad(aa, xf, yf), Noise2D.grad(ba, xf - 1, yf), u)
    const x2 = lerpN(Noise2D.grad(ab, xf, yf - 1), Noise2D.grad(bb, xf - 1, yf - 1), u)
    return lerpN(x1, x2, v) * 1.4
  }

  /** Fractal Brownian motion. */
  fbm(x: number, y: number, octaves: number, lacunarity = 2.03, gain = 0.5): number {
    let sum = 0
    let amp = 1
    let norm = 0
    let fx = x
    let fy = y
    for (let i = 0; i < octaves; i++) {
      sum += this.sample(fx, fy) * amp
      norm += amp
      amp *= gain
      fx *= lacunarity
      fy *= lacunarity
    }
    return norm > 0 ? sum / norm : 0
  }

  /**
   * Ridged multifractal. Produces sharp crests and rounded troughs — the shape
   * of eroded alpine terrain, and what makes spines and drainages read.
   */
  ridged(x: number, y: number, octaves: number, lacunarity = 2.07, gain = 0.5): number {
    let sum = 0
    let amp = 1
    let norm = 0
    let fx = x
    let fy = y
    for (let i = 0; i < octaves; i++) {
      const n = 1 - Math.abs(this.sample(fx, fy))
      sum += n * n * amp
      norm += amp
      amp *= gain
      fx *= lacunarity
      fy *= lacunarity
    }
    return norm > 0 ? (sum / norm) * 2 - 1 : 0
  }
}

function lerpN(a: number, b: number, t: number): number {
  return a + (b - a) * t
}
